import { computed } from "vue";

import { UNASSIGNED_ASSIGNEE_LABEL } from "~/components/visualization/constants";
import { PRIORITY_OPTIONS, UNPRIORITIZED_PRIORITY_LABEL } from "~/constants/priority";
import { TSHIRT_SIZES_INCLUDING_NULL, UNSIZED_SIZE_LABEL } from "~/constants/size";
import type { Priority, TshirtSize } from "~/shared/enums";
import type { Status, Task } from "~/shared/types";
import { useDataStore, useTenantStore } from "~/stores";
import { intersectionOfAllSets, unionOfAllSets } from "~/utils/common";
import { getUserDisplayName } from "~/utils/user";

import type { Actions } from "..";

type KeyLabel<T = string> = { key: T; label: string };

const UNASSIGNED_ASSIGNEE = { key: null, label: UNASSIGNED_ASSIGNEE_LABEL };

/** Getter related actions */
export default (actions: Actions) => ({
  // TODO for these, should we not include the currently selected options?
  getPossibleDartboards: () => {
    const dataStore = useDataStore();
    return dataStore.getDartboardList();
  },
  getPossiblePriorities: (): KeyLabel<Priority | null>[] =>
    PRIORITY_OPTIONS.map((key) => ({ key, label: key?.toString() ?? UNPRIORITIZED_PRIORITY_LABEL })),
  getPossibleStatuses: async (): Promise<Status[]> => {
    const dataStore = useDataStore();

    let currents;
    try {
      currents = actions.visualization.getCurrentRows();
    } catch {
      return [];
    }
    if (currents.length === 0) {
      return [];
    }
    const tasks = await actions.visualization.getCurrentRowsTasks();
    const taskKinds = computed(() =>
      dataStore.getTaskKindsByDuidsOrdered([...new Set(tasks.map((task) => task.kindDuid))])
    );
    return dataStore.getStatusList(dataStore.defaultStatusProperty, taskKinds.value);
  },
  getPossibleSizes: (): KeyLabel<TshirtSize | null>[] =>
    TSHIRT_SIZES_INCLUDING_NULL.map((key) => ({ key, label: key?.toString() ?? UNSIZED_SIZE_LABEL })),
  copyTaskBranchNames: async (tasks?: Task[]) => {
    actions.task.copyBranchNames(tasks ?? (await actions.visualization.getCurrentRowsTasks()));
  },
  copyTaskLinks: async (tasks?: Task[]) => {
    actions.task.copyLinks(tasks ?? (await actions.visualization.getCurrentRowsTasks()));
  },
  copyTaskAsPrompt: async (tasks?: Task[]) => {
    actions.task.copyAsPrompt(tasks ?? (await actions.visualization.getCurrentRowsTasks()));
  },
  getCurrentAssignees: async () => {
    const dataStore = useDataStore();

    const userItems = computed(() =>
      dataStore.getUserList().map((e) => ({ key: e.duid, label: getUserDisplayName(e) }))
    );

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return [];
    }
    const sharedAssigneeDuids = unionOfAllSets(tasks.map((e) => e.assigneeDuids).map((e) => new Set(e)));
    return userItems.value.filter((e) => sharedAssigneeDuids.has(e.key));
  },
  getCurrentReviewers: async () => {
    const dataStore = useDataStore();

    const userItems = computed(() =>
      dataStore.getUserList().map((e) => ({ key: e.duid, label: getUserDisplayName(e) }))
    );

    const tasks = await actions.visualization.getCurrentRowsTasks();
    if (tasks.length === 0) {
      return [];
    }
    const sharedReviewerDuids = unionOfAllSets(tasks.map((e) => e.reviewerDuids).map((e) => new Set(e)));
    return userItems.value.filter((e) => sharedReviewerDuids.has(e.key));
  },
  getCurrentTags: async () => {
    const dataStore = useDataStore();

    let tasks;
    try {
      tasks = await actions.visualization.getCurrentRowsTasks();
    } catch {
      return [];
    }
    if (tasks.length === 0) {
      return [];
    }
    const sharedTags = intersectionOfAllSets(tasks.map((e) => e.tagDuids).map((e) => new Set(e)));
    return dataStore.getOptionsByDuidsOrdered([...sharedTags], dataStore.defaultTagsProperty);
  },
  getPossibleAssignees: async () => {
    const dataStore = useDataStore();
    const tenantStore = useTenantStore();

    let tasks;
    try {
      tasks = await actions.visualization.getCurrentRowsTasks();
    } catch {
      return [];
    }
    if (tasks.length === 0) {
      return [];
    }
    const sharedAssigneeDuids = intersectionOfAllSets(
      tasks
        .map((e) => (tenantStore.multipleAssigneesEnabled ? e.assigneeDuids : e.assigneeDuids?.slice(0, 1)))
        .map((e) => new Set(e))
    );

    const userItems = computed(() =>
      dataStore.getRelevantUsersByTasks(tasks).map((e) => ({ key: e.duid, label: getUserDisplayName(e) }))
    );

    // filter out only assignee(s) that are shared by all tasks
    const result: { key: string | null; label: string }[] = userItems.value.filter(
      (e) => e.key !== null && !sharedAssigneeDuids.has(e.key)
    );
    if (!tenantStore.multipleAssigneesEnabled && !tasks.every((e) => !e.assigneeDuids?.length)) {
      result.unshift(UNASSIGNED_ASSIGNEE);
    }
    return result;
  },
  getPossibleReviewers: async () => {
    const dataStore = useDataStore();
    const tenantStore = useTenantStore();

    let tasks;
    try {
      tasks = await actions.visualization.getCurrentRowsTasks();
    } catch {
      return [];
    }
    if (tasks.length === 0) {
      return [];
    }
    const sharedReviewerDuids = intersectionOfAllSets(
      tasks
        .map((e) => (tenantStore.multipleReviewersEnabled ? e.reviewerDuids : e.reviewerDuids?.slice(0, 1)))
        .map((e) => new Set(e))
    );

    const userItems = computed(() =>
      dataStore.getRelevantUsersByTasks(tasks).map((e) => ({ key: e.duid, label: getUserDisplayName(e) }))
    );

    // filter out only reviewer(s) that are shared by all tasks
    const result: { key: string | null; label: string }[] = userItems.value.filter(
      (e) => e.key !== null && !sharedReviewerDuids.has(e.key)
    );
    if (!tenantStore.multipleReviewersEnabled && !tasks.every((e) => !e.reviewerDuids?.length)) {
      result.unshift(UNASSIGNED_ASSIGNEE);
    }
    return result;
  },
  getPossibleTags: async () => {
    const dataStore = useDataStore();

    let tasks;
    try {
      tasks = await actions.visualization.getCurrentRowsTasks();
    } catch {
      return [];
    }
    if (tasks.length === 0) {
      return [];
    }
    const sharedTags = intersectionOfAllSets(tasks.map((e) => e.tagDuids).map((e) => new Set(e)));
    // filter out only tags that are shared by all tasks
    return dataStore.getOptionList(dataStore.defaultTagsProperty).filter((e) => !sharedTags.has(e.duid));
  },
});
